/**
 * Elevator Prompts Component
 * Handles elevator navigation between floors (Rooftop and VIP)
 */
import React from 'react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const ElevatorPrompts = ({
  showElevatorPrompt,
  setShowElevatorPrompt,
  showReturnElevatorPrompt,
  setShowReturnElevatorPrompt,
  CLUB_ROOMS,
  changeRoom
}) => {
  const goToRooftop = () => {
    const rooftopRoom = CLUB_ROOMS.find(r => r.id === 'rooftop');
    if (rooftopRoom) {
      changeRoom(rooftopRoom);
      toast.success('🏙️ Welcome to the Rooftop Terrace!');
    }
    setShowElevatorPrompt(false);
  };

  const goToVIP = () => {
    const vipRoom = CLUB_ROOMS.find(r => r.id === 'vip_lounge');
    if (vipRoom) {
      changeRoom(vipRoom);
      toast.success('👑 Welcome back to VIP Lounge!');
    }
    setShowReturnElevatorPrompt(false);
  };

  return (
    <>
      {/* Elevator to Rooftop Prompt */}
      {showElevatorPrompt && (
        <div 
          className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setShowElevatorPrompt(false)}
          data-testid="elevator-rooftop-overlay"
        >
          <div 
            className="bg-gradient-to-br from-slate-900 to-emerald-900/50 rounded-2xl p-6 max-w-sm w-full border border-emerald-500/30 shadow-2xl"
            onClick={e => e.stopPropagation()}
            data-testid="elevator-rooftop-modal"
          >
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-emerald-500/20 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white">Rooftop Access</h3>
              <p className="text-white/70 text-sm">
                Would you like to take the elevator to the exclusive Rooftop Terrace? 
              </p>
              <p className="text-emerald-400 text-xs">
                ✨ Features city views, trees, string lights & starry sky
              </p>
              <div className="flex gap-3 pt-2">
                <Button 
                  onClick={() => setShowElevatorPrompt(false)}
                  variant="outline"
                  className="flex-1 border-white/20 text-white/70 hover:bg-white/10"
                  data-testid="elevator-stay-btn"
                >
                  Stay Here
                </Button>
                <Button 
                  onClick={goToRooftop}
                  className="flex-1 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500"
                  data-testid="elevator-go-rooftop-btn"
                >
                  Go to Rooftop
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Return Elevator Prompt (Rooftop -> VIP) */}
      {showReturnElevatorPrompt && (
        <div 
          className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowReturnElevatorPrompt(false)}
          data-testid="elevator-return-overlay"
        >
          <div 
            className="bg-gradient-to-br from-gray-900 to-blue-900 rounded-2xl p-6 max-w-sm border border-blue-500/30 shadow-2xl"
            onClick={e => e.stopPropagation()}
            data-testid="elevator-return-modal"
          >
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-blue-500/20 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white">Return to VIP Lounge</h3>
              <p className="text-white/70 text-sm">
                Take the elevator back down to the VIP Lounge?
              </p>
              <div className="flex gap-3 pt-2">
                <Button 
                  onClick={() => setShowReturnElevatorPrompt(false)}
                  variant="outline"
                  className="flex-1 border-white/20 text-white/70 hover:bg-white/10"
                  data-testid="elevator-stay-rooftop-btn"
                >
                  Stay Here
                </Button>
                <Button 
                  onClick={goToVIP}
                  className="flex-1 bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-500 hover:to-yellow-500"
                  data-testid="elevator-go-vip-btn"
                >
                  Go to VIP
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ElevatorPrompts;
